import { PropsWithChildren, createContext, useEffect, useState } from "react";

import {
  clearLegacyLocalStorageRegionKeySource,
  consumeRegionKeyJustInjected,
  getRegionKey,
  getRegionKeySource,
  isSupportedRegion,
  setRegionKeyJustInjected,
  setRegionKeySource,
} from "../region-utils";

/** Region context - provides a region.  */
export const RegionContext = createContext("");

function isAuthRedirectCallbackPath(pathname: string) {
  const pathParts = pathname.split("/").filter(Boolean);
  const [firstPart, secondPart] = pathParts;

  return (
    firstPart === "callback" ||
    (!!firstPart && isSupportedRegion(firstPart) && secondPart === "callback")
  );
}

/**
 * Provides the region key to the app. If the region is not supported, it will inject a supported region into the URL and redirect.
 * @param {PropsWithChildren} props
 * @returns {React.ReactElement}
 */
export function RegionProvider(props: PropsWithChildren) {
  const [region, setRegion] = useState(getRegionKey());

  function handleUrlChange() {
    const urlRegion = window.location.pathname.split("/")[1];

    if (!isSupportedRegion(urlRegion)) {
      // FP-1027: region-less `/callback` is an Auth0 redirect_uri artifact — inject
      // the prefix without setting `inferred` or the handshake flag.
      if (urlRegion === "callback") {
        window.location.replace(
          `/${getRegionKey()}${window.location.pathname}${window.location.search}`
        );
        return;
      }

      setRegionKeySource("inferred");
      // FIRE-200: handshake flag confirms `inferred` reflects *this* page-load's
      // injection, not a leftover from an earlier in-tab nav. The next
      // RegionProvider mount consumes the flag; if absent, any `inferred` in
      // sessionStorage is stale and gets promoted to `explicit` below.
      setRegionKeyJustInjected();
      window.location.replace(
        `/${getRegionKey()}${window.location.pathname}${window.location.search}`
      );
      return;
    }

    // URL has a supported region prefix. If the previous load injected the
    // prefix, the handshake flag is still present — preserve `inferred` so
    // AuthProvider can act on it. Auth0 callbacks can arrive one page load
    // later, after the one-shot flag was consumed; preserve `inferred` there
    // too and let AuthProvider clear it after the callback is handled.
    // Otherwise the URL was user-driven (typed, bookmarked, in-session nav)
    // and any leftover `inferred` is stale — promote to `explicit` so
    // post-auth uses the normal priority chain.
    const wasJustInjected = consumeRegionKeyJustInjected();
    if (
      getRegionKeySource() === "inferred" &&
      !wasJustInjected &&
      !isAuthRedirectCallbackPath(window.location.pathname)
    ) {
      setRegionKeySource("explicit");
    }

    setRegion(getRegionKey());
  }

  useEffect(() => {
    // FIRE-200: drop any stale `region-key-source` that pre-FIRE-200 builds
    // wrote to localStorage so it can't bleed into this tab's signal.
    clearLegacyLocalStorageRegionKeySource();

    handleUrlChange();

    window.addEventListener("popstate", handleUrlChange);

    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  return (
    <RegionContext.Provider value={region}>
      {props.children}
    </RegionContext.Provider>
  );
}
